import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Modal, Icon, Alert } from "@ann/web-components";
import {
  HeaderEmailModalContainer,
  EmailItemsWrapper,
  AddEmailContainer,
  EmailInputContainer,
  EmailItemContainer,
  RadioButtonSection,
  DeleteIconSection,
  RadioButton,
  ValidationMessage,
  FloatingAlertContainer,
} from "./HeaderEmailModal.styled";
import InputText from "../../../../../elements/v2/TextInput/TextInput.jsx";
import { useContacts } from "../../context/ContactsContext";

const HeaderEmailModal = ({ isOpen, onClose, onRequestRemove }) => {
  const {
    emails: contextEmails,
    createEmail,
    updateEmail,
    deleteEmail,
    selectedContact,
    fetchContactEmails,
    applyPrimaryEmailLocally,
  } = useContacts();
  const contactId = selectedContact?.partyUuid;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailErrors, setEmailErrors] = useState({});
  const [validEmails, setValidEmails] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSyncedOnOpen, setHasSyncedOnOpen] = useState(false);

  const emailRegex = useMemo(
    () => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    []
  );

  const successTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen && contactId && !hasSyncedOnOpen) {
      fetchContactEmails(contactId);
      setHasSyncedOnOpen(true);
    }
    if (!isOpen) {
      setHasSyncedOnOpen(false);
      setShowSuccessAlert(false);
      setSuccessMessage("");
      setStatusMessage("");
      setEmails([]);
      setSelectedEmailId(null);
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    }
  }, [isOpen, contactId, hasSyncedOnOpen, fetchContactEmails]);

  useEffect(() => {
    if (
      isOpen &&
      contextEmails &&
      contextEmails.length > 0 &&
      emails.length === 0
    ) {
      const mapped = contextEmails.map((e) => ({
        id: e.partyEmailUuid,
        email: e.emailAddress,
        isEditing: false,
        isPrimary: e.isPrimary,
        isNew: false,
      }));
      setEmails(mapped);
      const primary = mapped.find((e) => e.isPrimary);
      if (primary) setSelectedEmailId(primary.id);
    }
  }, [isOpen, contextEmails, emails.length]);

  useEffect(() => {
    const existingStyle = document.getElementById("modal-custom-width");
    if (isOpen && !existingStyle) {
      const style = document.createElement("style");
      style.id = "modal-custom-width";
      style.textContent = `
        [role="dialog"] {
          width: 500px !important;
          max-width: 500px !important;
          min-width: 500px !important;
        }
      `;
      document.head.appendChild(style);
    } else if (!isOpen && existingStyle) {
      existingStyle.remove();
    }
    return () => {
      const cleanupStyle = document.getElementById("modal-custom-width");
      if (cleanupStyle) cleanupStyle.remove();
    };
  }, [isOpen]);

  const handleEmailSelection = useCallback((value) => {
    setSelectedEmailId(value);
  }, []);

  const handleDeleteEmail = useCallback(
    (emailId) => {
      if (!contactId) return;

      const emailToDelete = emails.find((e) => e.id === emailId);
      if (emailToDelete?.isPrimary) {
        setStatusMessage("");
        setShowErrorAlert(true);
        setEmailErrors((prev) => ({
          ...prev,
          [emailId]: "Cannot delete primary email",
        }));
        return;
      }

      if (onRequestRemove) {
        onRequestRemove(emailId, emailToDelete?.email ?? "");
        return;
      }

      setIsDeleting(true);
      (async () => {
        try {
          await deleteEmail(contactId, emailId);
          setEmails((prev) => prev.filter((e) => e.id !== emailId));
          if (selectedEmailId === emailId) {
            const remaining = emails.filter((e) => e.id !== emailId);
            if (remaining.length > 0) setSelectedEmailId(remaining[0].id);
          }
          setStatusMessage("");
          setSuccessMessage("");
          setShowSuccessAlert(false);
        } catch (err) {
          setShowErrorAlert(true);
          setStatusMessage("Error deleting email. Please try again.");
          console.error("Error deleting email:", err);
        } finally {
          setIsDeleting(false);
        }
      })();
    },
    [contactId, deleteEmail, selectedEmailId, emails, onRequestRemove]
  );

  const handleEmailChange = useCallback(
    (emailId, newValue) => {
      const trimmed = newValue.trim();
      const isValid = trimmed && emailRegex.test(trimmed);
      setEmails((prev) =>
        prev.map((e) => (e.id === emailId ? { ...e, email: newValue } : e))
      );
      setValidEmails((prev) => ({ ...prev, [emailId]: isValid }));
      if (isValid) {
        setEmailErrors((prev) => {
          const updated = { ...prev };
          delete updated[emailId];
          return updated;
        });
      }
    },
    [emailRegex]
  );

  const handleConfirmEmail = useCallback((emailId) => {
    setEmails((prev) =>
      prev.map((e) => (e.id === emailId ? { ...e, isEditing: false } : e))
    );
    setEmailErrors((prev) => {
      const updated = { ...prev };
      delete updated[emailId];
      return updated;
    });
  }, []);

  const handleKeyDown = useCallback(
    (e, emailId) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      const email = emails.find((em) => em.id === emailId)?.email.trim();
      if (!email) {
        setEmailErrors((prev) => ({ ...prev, [emailId]: "Email is required" }));
        return;
      }
      if (!emailRegex.test(email)) {
        setEmailErrors((prev) => ({
          ...prev,
          [emailId]: "The Email is incorrect, please try with another",
        }));
        return;
      }
      handleConfirmEmail(emailId);
    },
    [emails, emailRegex, handleConfirmEmail]
  );

  const canAdd = useMemo(
    () => !emails.some((e) => e.isEditing || emailErrors[e.id]),
    [emails, emailErrors]
  );
  const canSave = useMemo(() => {
    const hasEditing = emails.some((e) => e.isEditing);
    const hasErrors = Object.values(emailErrors).some(Boolean);
    return !hasEditing && !hasErrors;
  }, [emails, emailErrors]);

  const handleAddEmail = useCallback(() => {
    if (!canAdd) {
      setShowErrorAlert(true);
      return;
    }
    const newId = `email${Date.now()}`;
    setEmails((prev) => [
      ...prev,
      { id: newId, email: "", isEditing: true, isNew: true },
    ]);
  }, [canAdd]);

  const handleSave = async () => {
    if (isSaving) return;
    if (!contactId) return;

    setIsSaving(true);
    try {
      const emailsToCreate = emails.filter(
        (e) =>
          e.isNew && (validEmails[e.id] || (e.email && e.email.trim() !== ""))
      );

      const emailsToUpdate = emails.filter((e) => {
        if (e.isNew) return false;
        if (e.isEditing && validEmails[e.id]) return true;
        const original = contextEmails.find((ce) => ce.partyEmailUuid === e.id);
        if (original) {
          const newIsPrimary = selectedEmailId === e.id;
          if (original.isPrimary !== newIsPrimary) return true;
        }
        return false;
      });

      const createResults = await Promise.allSettled(
        emailsToCreate.map((item) => {
          const emailLower = (item.email || "").trim().toLowerCase();
          if (!emailRegex.test(emailLower)) {
            return Promise.reject(new Error("Invalid email format"));
          }
          const isPrimary = selectedEmailId === item.id;
          return createEmail(contactId, {
            email_type: "PERSONAL",
            email_address: emailLower,
            is_primary: isPrimary,
          });
        })
      );

      const updateResults = await Promise.allSettled(
        emailsToUpdate.map((item) => {
          const original = contextEmails.find(
            (ce) => ce.partyEmailUuid === item.id
          );
          if (!original) return Promise.resolve();
          const emailLower = (item.email || "").trim().toLowerCase();
          if (!emailRegex.test(emailLower)) {
            return Promise.reject(new Error("Invalid email format"));
          }
          const updateData = {};
          const newIsPrimary = selectedEmailId === item.id;
          if (original.emailAddress.toLowerCase() !== emailLower) {
            updateData.email_address = emailLower;
          }
          if (original.isPrimary !== newIsPrimary) {
            updateData.is_primary = newIsPrimary;
          }
          if (Object.keys(updateData).length === 0) return Promise.resolve();
          return updateEmail(contactId, item.id, updateData);
        })
      );

      const failures = [...createResults, ...updateResults].filter(
        (r) => r.status === "rejected"
      );
      if (failures.length) {
        setShowErrorAlert(true);
        setStatusMessage(
          `Some changes couldn't be saved (${failures.length}). Please review and try again.`
        );
        return;
      }

      const selectedLocal = emails.find((e) => e.id === selectedEmailId);
      const originalSelected = contextEmails.find(
        (ce) => ce.partyEmailUuid === selectedEmailId
      );
      const primaryChanged = originalSelected
        ? !originalSelected.isPrimary
        : !!selectedLocal?.isNew;

      if (primaryChanged) {
        applyPrimaryEmailLocally(
          selectedEmailId,
          selectedLocal?.email || originalSelected?.emailAddress || ""
        );
        setSuccessMessage("Primary contact updated successfully.");
        setShowSuccessAlert(true);
        successTimeoutRef.current = setTimeout(() => {
          setShowSuccessAlert(false);
          setSuccessMessage("");
          successTimeoutRef.current = null;
          handleClose();
        }, 1200);
      } else if (emailsToCreate.length > 0) {
        setSuccessMessage("New email successfully added");
        setShowSuccessAlert(true);
        successTimeoutRef.current = setTimeout(() => {
          setShowSuccessAlert(false);
          setSuccessMessage("");
          successTimeoutRef.current = null;
          handleClose();
        }, 1200);
      } else {
        handleClose();
      }
    } catch (err) {
      console.error("Error saving emails:", err);
      setShowErrorAlert(true);
      setStatusMessage("Error saving emails. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="medium"
        closeFromOutside={false}
      >
        <Modal.Header>
          <Modal.Header.Title>Emails</Modal.Header.Title>
          <Modal.Header.Description>
            These are other emails of this client. You can select which one is
            the main one.
          </Modal.Header.Description>
        </Modal.Header>

        <Modal.Body>
          <HeaderEmailModalContainer>
            <EmailItemsWrapper>
              {emails.map((emailItem) => {
              const isSelected = selectedEmailId === emailItem.id;
              const isValid = validEmails[emailItem.id];
              const hasError = !!emailErrors[emailItem.id];
              const shouldShowMessage =
                emailItem.isEditing && isValid && !hasError;

              const inlineMessage = hasError
                ? emailErrors[emailItem.id]
                : shouldShowMessage
                ? "Press enter to add it"
                : null;

              return (
                <React.Fragment key={emailItem.id}>
                  <EmailItemContainer
                    hasError={hasError}
                    isValid={isValid}
                    isEditing={emailItem.isEditing}
                  >
                    <RadioButtonSection>
                      <RadioButton
                        type="radio"
                        name="emailSelection"
                        value={emailItem.id}
                        checked={isSelected}
                        onChange={(e) => handleEmailSelection(e.target.value)}
                      />
                    </RadioButtonSection>

                    <EmailInputContainer>
                      {emailItem.isEditing ? (
                        <InputText
                          type="email"
                          placeholder="Enter email"
                          value={emailItem.email}
                          onChange={(e) =>
                            handleEmailChange(emailItem.id, e.target.value)
                          }
                          onKeyDown={(e) => handleKeyDown(e, emailItem.id)}
                          autoFocus
                          height="36px"
                          width="100%"
                          displayError={false}
                          displaySuccess={false}
                        />
                      ) : (
                        <span>{emailItem.email}</span>
                      )}
                    </EmailInputContainer>

                    <DeleteIconSection>
                      <Icon
                        name="delete"
                        onClick={() => handleDeleteEmail(emailItem.id)}
                        style={{
                          cursor: isDeleting ? "not-allowed" : "pointer",
                          opacity: isDeleting ? 0.5 : 1,
                        }}
                      />
                    </DeleteIconSection>
                  </EmailItemContainer>
                  {inlineMessage && (
                    <ValidationMessage type={hasError ? "error" : "success"}>
                      {inlineMessage}
                    </ValidationMessage>
                  )}
                </React.Fragment>
              );
            })}
            </EmailItemsWrapper>

            <AddEmailContainer onClick={handleAddEmail} disabled={!canAdd}>
              <Icon name="plus" />
            </AddEmailContainer>
          </HeaderEmailModalContainer>
        </Modal.Body>

        <Modal.Actions align="end">
          <Modal.Button
            label={isSaving ? "Saving..." : "Save"}
            onClick={handleSave}
            disabled={!canSave || isSaving}
          />
        </Modal.Actions>
      </Modal>

      {(showSuccessAlert || showErrorAlert) && (
        <FloatingAlertContainer>
          {showSuccessAlert && (
            <Alert
              severity="success"
              title={
                successMessage.includes("Primary")
                  ? "Primary contact updated successfully."
                  : "New email successfully added"
              }
              message={successMessage}
              onClose={() => {
                setShowSuccessAlert(false);
                setSuccessMessage("");
                if (successTimeoutRef.current) {
                  clearTimeout(successTimeoutRef.current);
                  successTimeoutRef.current = null;
                }
              }}
            />
          )}
          {showErrorAlert && (
            <Alert
              severity="error"
              title="Please complete or fix the pending email fields."
              message={
                statusMessage ||
                "You must resolve all open or invalid fields before continuing."
              }
              onClose={() => {
                setShowErrorAlert(false);
                setStatusMessage("");
              }}
            />
          )}
        </FloatingAlertContainer>
      )}
    </>
  );
};

export default HeaderEmailModal;
